import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, ArrowRight, Users, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

/* 2026-09 R13 item 5 — the shared "What happens next" explanation, built
   once and mounted wherever an employee needs to understand a customer's
   real signup-journey state: Customer 360, Pipeline rows/cards (expandable),
   the Case overview, and the outcome forms (Case workflow outcome and
   consultation outcome). Fetches GET /opportunities/{id}/what-happens-next —
   every fact shown here is derived server-side from the SAME governed
   records/functions the real transition/activation code uses (see
   backend/signup_journey_status.py); this component never infers or computes
   anything itself, it only renders what the backend already decided, so every
   surface shows the identical status, completed and missing requirements,
   next actor and next action for the same opportunity. It is READ-ONLY: it
   issues one GET and nothing else.

   A failed request is NEVER rendered as nothing: an absent card must not be
   read as "nothing is pending", so a load failure shows an explicit
   "unavailable" state with a Retry. */

export const MILESTONE_LABEL = {
  not_yet_engaged: "Not yet engaged",
  signup_intent_not_recorded: "Signup not yet initiated",
  awaiting_product_confirmation: "Awaiting product confirmation",
  awaiting_payment: "Awaiting payment",
  awaiting_leadership: "Awaiting Leadership approval",
  activation_approved_pending_handoff: "Approved — handover pending",
  case_active: "Handover completed",
};

const MILESTONE_TONE = {
  not_yet_engaged: "bg-slate-100 text-slate-700",
  signup_intent_not_recorded: "bg-slate-100 text-slate-700",
  awaiting_product_confirmation: "bg-amber-100 text-amber-800",
  awaiting_payment: "bg-amber-100 text-amber-800",
  awaiting_leadership: "bg-sky-100 text-sky-800",
  activation_approved_pending_handoff: "bg-orange-100 text-orange-800",
  case_active: "bg-emerald-100 text-emerald-800",
};

// The one request every surface makes. status: loading | ready | unavailable
export function useWhatHappensNext(opportunityId) {
  const [state, setState] = useState({ status: "loading", data: null, reason: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!opportunityId) return undefined;
    let cancelled = false;
    setState({ status: "loading", data: null, reason: null });
    api.get(`/opportunities/${opportunityId}/what-happens-next`)
      .then((r) => {
        if (cancelled) return;
        if (!r.data || r.data.error) setState({ status: "unavailable", data: null, reason: r.data?.error || "empty_response" });
        else setState({ status: "ready", data: r.data, reason: null });
      })
      .catch((e) => { if (!cancelled) setState({ status: "unavailable", data: null, reason: e?.response?.status ? `http_${e.response.status}` : "network" }); });
    return () => { cancelled = true; };
  }, [opportunityId, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

// The fields every surface must show identically; used by tests and by the browser check.
export const journeyFields = (d) => ({
  status: MILESTONE_LABEL[d.milestone] || d.milestone,
  summary: d.summary || "",
  complete: d.complete || [],
  missing: d.missing || [],
  nextActor: d.next_actor || null,
  nextAction: d.immediate_action || null,
  automatic: d.automatic_consequence || null,
});

export function WhatHappensNextPanel({ opportunityId, compact = false, surface = "customer-360" }) {
  const { status, data: d, reason, retry } = useWhatHappensNext(opportunityId);
  if (!opportunityId) return null;

  if (status === "loading") {
    return (
      <Card data-testid="what-happens-next-loading" data-surface={surface} className="border-dashed">
        <CardContent className="py-3 text-xs text-muted-foreground">Loading what happens next…</CardContent>
      </Card>
    );
  }
  if (status === "unavailable") {
    return (
      <Card data-testid="what-happens-next-unavailable" data-surface={surface} role="alert"
        className="border-[hsl(var(--warning))]/50">
        <CardContent className="py-3 text-sm flex items-start gap-2 flex-wrap">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-[hsl(var(--warning))] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-medium">What happens next is unavailable</p>
            <p className="text-xs text-muted-foreground" data-testid="whn-unavailable-text">
              We couldn't load this customer's journey status ({reason}). This does not mean nothing is pending —
              check the record directly or try again.
            </p>
          </div>
          <Button type="button" size="sm" variant="outline" data-testid="whn-retry" onClick={retry}>Retry</Button>
        </CardContent>
      </Card>
    );
  }

  const f = journeyFields(d);
  return (
    <Card data-testid="what-happens-next-panel" data-surface={surface} className={compact ? "border-dashed" : undefined}>
      <CardHeader className="pb-2 flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm font-heading">What happens next</CardTitle>
        <Badge data-testid="whn-status" className={MILESTONE_TONE[d.milestone] || "bg-slate-100 text-slate-700"}>
          {f.status}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground" data-testid="whn-summary">{f.summary}</p>
        {f.complete.length > 0 && (
          <div className="space-y-1" data-testid="whn-complete">
            {f.complete.map((c, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs" data-testid="whn-complete-item">
                <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
                <span>{c}</span>
              </div>
            ))}
          </div>
        )}
        {f.missing.length > 0 && (
          <div className="space-y-1" data-testid="whn-missing">
            {f.missing.map((m, i) => (
              <div key={i} className="flex items-start gap-1.5 text-xs" data-testid="whn-missing-item">
                <Circle className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
                <span>{m}</span>
              </div>
            ))}
          </div>
        )}
        {f.nextActor && f.nextAction && (
          <div className="flex items-start gap-1.5 rounded-md border bg-muted/40 p-2 text-xs" data-testid="whn-next">
            <Users className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span><b data-testid="whn-next-actor">{f.nextActor}</b>: <span data-testid="whn-next-action">{f.nextAction}</span></span>
          </div>
        )}
        {f.automatic && (
          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
            <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span data-testid="whn-automatic">{f.automatic}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// A lazy, read-only expander for list surfaces (Pipeline rows and cards): nothing is fetched until it is opened, so a
// board of hundreds of cards makes no extra requests. Opening shows the SAME panel the other surfaces show.
export function WhatHappensNextDisclosure({ opportunityId, surface = "pipeline", testId }) {
  const [open, setOpen] = useState(false);
  if (!opportunityId) return null;
  return (
    <div data-testid={testId || `whn-disclosure-${opportunityId}`} onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()} draggable={false}>
      <button type="button" aria-expanded={open}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground hover:underline"
        data-testid={`whn-toggle-${opportunityId}`} onClick={() => setOpen((o) => !o)}>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} What happens next
      </button>
      {open && <div className="mt-1.5"><WhatHappensNextPanel opportunityId={opportunityId} compact surface={surface} /></div>}
    </div>
  );
}
